import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuth, signOut } from 'firebase/auth';
import { app } from './firebase';
import { useNavigate } from 'react-router-dom'; 
import EnvironmentManager from '../config/EnvironmentManager';
import './CourseManagement.css';
import BackButton from "./BackButton";
import { Heart, ShoppingCart, Bell, Upload, Eye, Settings, CreditCard, RefreshCw, Globe, Play, Pause, Download, FileSpreadsheet, Home, BookOpen, Archive, User, ChevronRight, Languages } from "lucide-react";

const HelperPopover = ({ content, openOnMount = true }) => {
  const [isOpen, setIsOpen] = useState(openOnMount);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Auto-open on mount
  useEffect(() => {
    if (openOnMount) {
      setIsOpen(true);
    }
  }, [openOnMount]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const togglePopover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="helper-popover-wrapper">
      <button
        ref={triggerRef}
        className="helper-popover-trigger"
        onClick={togglePopover}
        title="Click for help"
      >
        ?
      </button>
      
      {isOpen && (
        <div ref={popoverRef} className="helper-popover-content">
          <div className="popover-header">
            <h4>How it works</h4>
            <button 
              className="popover-close"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="popover-body">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

const ThreeDotMenu = ({ 
  courseId, 
  onUpdate, 
  onDelete, 
  onArchive, 
  onUnarchive, 
  isArchived = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleUpdateClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onUpdate(courseId);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete(courseId);
  };

  const handleArchiveClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onArchive(courseId);
  };

  const handleUnarchiveClick = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onUnarchive(courseId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="three-dot-menu">
      <button 
        className="three-dot-button"
        onClick={toggleMenu}
      >
        ⋮
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {!isArchived ? (
            <>
              <button 
                className="dropdown-item update"
                onClick={handleUpdateClick}
              >
                Update
              </button>
              <button 
                className="dropdown-item archive"
                onClick={handleArchiveClick}
              >
                Archive
              </button>
              <button 
                className="dropdown-item delete"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </>
          ) : (
            <>
              <button 
                className="dropdown-item unarchive"
                onClick={handleUnarchiveClick}
              >
                Unarchive
              </button>
              <button 
                className="dropdown-item delete"
                onClick={handleDeleteClick}
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CollapsibleSidebar = ({ currentPage, onNavigate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const menuItems = [
    { id: 'manage-courses', label: 'Manage Courses', icon: Settings },
    { id: 'courses', label: 'View Courses', icon: Eye },
    { id: 'upload', label: 'Upload Courses', icon: Upload },
    { id: 'archive', label: 'Archived Courses', icon: Archive },
  ];

  return (
    <div 
      className={`manage-courses-sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="sidebar-content">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <button
              key={item.id}
              className={`sidebar-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => onNavigate(item.id)}
            >
              <IconComponent size={20} />
              {isExpanded && <span>{item.label}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ProfessionalBanner = () => {
  return (
    <div className="professional-banner">
      <div className="banner-content">
        <div className="banner-text">
          <h1>Manage Your Courses</h1>
          <p>Create, organize, and track all your audio courses in one place</p>
        </div>
        <div className="banner-features">
          <div className="feature-card">
            <Upload size={24} />
            <h3>Easy Upload</h3>
            <p>Upload Excel files to create courses instantly</p>
          </div>
          <div className="feature-card">
            <Eye size={24} />
            <h3>Track Progress</h3>
            <p>Monitor student engagement and course performance</p>
          </div>
          <div className="feature-card">
            <Archive size={24} />
            <h3>Organize</h3>
            <p>Archive and manage your course library efficiently</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManageCoursesHeader = ({ onNewCourse, searchQuery, onSearch }) => {
  return (
    <div className="manage-courses-header">
      <div className="mobile-app-banner">
        <div className="app-banner-content">
          <div className="app-info">
            <h3>Get a better mobile experience with the MHMV app</h3>
            <p>View key course metrics, reply to your students, and get instant notifications.</p>
          </div>
          <div className="app-buttons">
            <button className="get-app-btn">Get the app</button>
            <button className="dismiss-btn">Dismiss</button>
          </div>
        </div>
      </div>
      
      <div className="courses-controls">
        <div className="search-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search your courses"
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              className="courses-search-input"
            />
          </div>
        </div>
        
        <div className="controls-right">
          <select className="sort-dropdown">
            <option>Newest</option>
            <option>Oldest</option>
            <option>Alphabetical</option>
          </select>
          <button className="new-course-btn" onClick={onNewCourse}>
            New Course
          </button>
        </div>
      </div>
    </div>
  );
};

const CoursesGridView = ({
  courses,
  onCourseSelect,
  onUpdateCourse,
  onDeleteCourse,
  onArchiveCourse,
  onUnarchiveCourse,
  isArchiveView = false,
  archivingCourseId = null,
  showOptions = true 
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (idx) => setExpandedIndex((prev) => (prev === idx ? null : idx));

  const getHighlights = (course) => {
    const pts = [];
    if (course.level) pts.push(`Level: ${course.level}`);
    if (course.language) pts.push(`Language: ${course.language}`);
    if (course.category) pts.push(`Category: ${course.category}`);
    if (course.instructor) pts.push(`By ${course.instructor}`);
    return pts.slice(0, 3);
  };

  return (
    <div className="courses-grid-view-full udemy-grid">
      <div className="courses-grid-full udemy-grid-inner">
        {courses.map((course, index) => {
          const highlights = getHighlights(course);
          const desc = (course.description || '').trim();
          const shortDesc = desc.length > 180 ? desc.slice(0, 180) + "..." : desc;
          const isArchiving = archivingCourseId === course.courseId;
          const randomBadge = Math.random() > 0.5 ? "Bestseller" : "Premium";

          return (
            <div
              key={course.courseId || index}
              className={`course-card-full udemy-card ${isArchiveView ? "archived-course" : ""} ${isArchiving ? "archiving" : ""}`}
              onClick={() => onCourseSelect(course)}
            >
              {showOptions && (
                <ThreeDotMenu
                  courseId={course.courseId}
                  onUpdate={onUpdateCourse}
                  onDelete={onDeleteCourse}
                  onArchive={onArchiveCourse}
                  onUnarchive={onUnarchiveCourse}
                  isArchived={isArchiveView || course.archived_course}
                />
              )}

              <div className="course-card-header-full udemy-thumb">
                <div className="course-image-full">
                  <div className="course-image-placeholder">
                    {course.title?.[0]?.toUpperCase() || "C"}
                  </div>
                </div>
                {!isArchiveView && (
                  <div className={`course-badge-ribbon ${randomBadge.toLowerCase()}`}>
                    {randomBadge}
                  </div>
                )}
                {isArchiveView && <div className="archive-badge-full">Archived</div>}
              </div>

              <div className="course-card-body-full udemy-body">
                <h3 className="course-card-title-full udemy-title">{course.title || `Course ${index + 1}`}</h3>
                {course.instructor && <p className="course-instructor-full udemy-instructor">By {course.instructor}</p>}
                <div className="udemy-meta-row">
                  {course.price && <span className="udemy-price">{course.price}</span>}
                </div>
              </div>

              <div className="course-card-footer-full udemy-footer">
                <button className="view-course-button-full" onClick={() => onCourseSelect(course)}>
                  View Details
                </button>
              </div>

              <div
                className={`udemy-hover-panel ${expandedIndex === index ? "expanded" : ""}`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="udemy-hover-content">
                  <div className="udemy-hover-title">{course.title}</div>
                  <div className="udemy-hover-desc">{shortDesc}</div>
                  {highlights.length > 0 && (
                    <ul className="udemy-hover-list">
                      {highlights.map((pt, i) => (
                        <li key={i}>• {pt}</li>
                      ))}
                    </ul>
                  )}
                  <button className="udemy-hover-cta" onClick={() => console.log('Add to Cart clicked')}>
                    Add to Cart
                  </button>
                </div>
              </div>

              <button className="udemy-mobile-expand" onClick={() => toggleExpand(index)}>
                {expandedIndex === index ? "Hide details" : "Quick view"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LanguageSelector = ({ 
  courseId, 
  user, 
  onLanguageSelect, 
  onConversionComplete,
  currentLanguage = 'en-US'
}) => {
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [converting, setConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(null);
  const [convertedLanguages, setConvertedLanguages] = useState([]);
  const [conversionStatus, setConversionStatus] = useState({});
  const [showConvertConfirmation, setShowConvertConfirmation] = useState(false);
  const getLanguageFlag = (languageCode) => {
    if (languageCode.endsWith('-US')) return '🇺🇸';
    if (languageCode.endsWith('-IN')) return '🇮🇳';
    if (languageCode.endsWith('-UK')) return '🇬🇧';
    if (languageCode.endsWith('-CA')) return '🇨🇦';
    if (languageCode.endsWith('-AU')) return '🇦🇺';
    return '🌐';
  };
  
  const getIdToken = useCallback(async () => {
    if (user) {
      try {
        const idToken = await user.getIdToken(true);
        return idToken;
      } catch (error) {
        console.error('Failed to get ID token:', error);
        try {
          const idToken = await user.getIdToken();
          return idToken;
        } catch (secondError) {
          console.error('Failed to get ID token even without force:', secondError);
          return null;
        }
      }
    }
    return null;
  }, [user]);

  const fetchAvailableVoices = useCallback(async () => {
    try {
      const idToken = await user.getIdToken();
      const response = await axios.get(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getAvailableVoices}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching available voices:', error);
      return [];
    }
  }, [user]);

  useEffect(() => {
    const loadLanguages = async () => {
      if (user) {
        const languages = await fetchAvailableVoices();
        setAvailableLanguages(languages);
        
        const status = {};
        languages.forEach(lang => {
          status[lang.languageCode] = {
            converted: false,
            converting: false,
            progress: 0,
            estimatedTime: null
          };
        });
        setConversionStatus(status);
      }
    };
    loadLanguages();
  }, [user, fetchAvailableVoices]);
  

  const getLanguageDisplayName = (languageCode) => {
    const languageNames = {
      'en-US': 'English ',
      'en-IN': 'English',
      'hi-IN': 'Hindi',
      'ta-IN': 'Tamil ',
      'te-IN': 'Telugu ',
      'kn-IN': 'Kannada',
      'ml-IN': 'Malayalam ',
      'mr-IN': 'Marathi',
      'gu-IN': 'Gujarati',
      'bn-IN': 'Bengali',
      'pa-IN': 'Punjabi',
      'ur-IN': 'Urdu'
    };
    return languageNames[languageCode] || languageCode;
  };

const getCountryCode = (languageCode) => {
  if (!languageCode) return '';
  const parts = languageCode.split('-');
  return parts.length > 1 ? parts[1] : '';
};

const getCountryFlag = (countryCode) => {
  const flags = {
    'US': '🇺🇸',
    'IN': '🇮🇳',
    'UK': '🇬🇧',
    'CA': '🇨🇦',
    'AU': '🇦🇺',
  };
  return flags[countryCode] || `[${countryCode}]`;
};


const getCardDisplayName = (languageCode) => {
  const simpleNames = {
    'en-US': 'English',
    'en-IN': 'English',
    'hi-IN': 'Hindi',
    'ta-IN': 'Tamil',
    'te-IN': 'Telugu',
    'kn-IN': 'Kannada',
    'ml-IN': 'Malayalam',
    'mr-IN': 'Marathi',
    'gu-IN': 'Gujarati',
    'bn-IN': 'Bengali',
    'pa-IN': 'Punjabi',
    'ur-IN': 'Urdu'
  };
  return simpleNames[languageCode] || languageCode;
};

  const getDropdownDisplayName = (languageCode) => {
    const languageNames = {
  'en-US': 'English - EN',
  'en-IN': 'English (India) - EN',
  'hi-IN': 'Hindi (हिन्दी) - HI',
  'ta-IN': 'Tamil (தமிழ்) - TA',
  'te-IN': 'Telugu (తెలుగు) - TE',
  'kn-IN': 'Kannada (ಕನ್ನಡ) - KN',
  'ml-IN': 'Malayalam (മലയാളം) - ML',
  'mr-IN': 'Marathi (मराठी) - MR',
  'gu-IN': 'Gujarati (ગુજરાતી) - GU',
  'bn-IN': 'Bengali (বাংলা) - BN',
  'pa-IN': 'Punjabi (ਪੰਜਾਬੀ) - PA',
  'ur-IN': 'Urdu (اردو) - UR',
};

    return languageNames[languageCode] || languageCode;
  };

  const handleLanguageSelect = (languageCode) => {
    setSelectedLanguage(languageCode);
    if (onLanguageSelect) {
      onLanguageSelect(languageCode);
    }
  };

  const handleConvertClick = () => {
    if (!selectedLanguage) {
      alert("Please select a language first");
      return;
    }
    
    if (isLanguageConverted(selectedLanguage)) {
      alert(`${getLanguageDisplayName(selectedLanguage)} is already converted`);
      return;
    }
    
    setShowConvertConfirmation(true);
  };

  const confirmConversion = async () => {
    setShowConvertConfirmation(false);
    await handleConvertToSpeech(selectedLanguage);
  };

  const handleConvertToSpeech = async (languageCode) => {
    if (!courseId || !user) {
      alert("Please log in to convert text to speech");
      return;
    }
    
    setConverting(true);
    
    setConversionStatus(prev => ({
      ...prev,
      [languageCode]: {
        ...prev[languageCode],
        converting: true,
        progress: 0,
        estimatedTime: 'Calculating...'
      }
    }));
    
    setConversionProgress({ 
      language: languageCode,
      message: "Starting conversion...", 
      percent: 0,
      currentItem: 0,
      totalItems: 0,
      estimatedTime: null
    });
    
    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        throw new Error("No authentication token available");
      }
      
      const endpoint = `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.textToSpeech}/${courseId}?languageCode=${languageCode}`;
      
      setConversionProgress(prev => ({
        ...prev,
        message: "Converting all course text to speech...",
        percent: 30
      }));
      
      const response = await axios.post(endpoint, {}, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        timeout: 300000,
      });

      setConversionProgress({ 
        language: languageCode,
        message: "Conversion complete! Refreshing course...", 
        percent: 100,
      });
      
      if (onConversionComplete) {
        await onConversionComplete();
      }

      setConversionStatus(prev => ({
        ...prev,
        [languageCode]: {
          ...prev[languageCode],
          converted: true,
          converting: false,
          progress: 100
        }
      }));
      
      setConvertedLanguages(prev => [...prev, languageCode]);
      
    } catch (err) {
      console.error("TTS Conversion Error:", err);
      
      setConversionStatus(prev => ({
        ...prev,
        [languageCode]: {
          ...prev[languageCode],
          converting: false,
          error: err.message
        }
      }));
      
      alert(`TTS conversion failed: ${err.message || "Unknown error"}`);
    } finally {
      setConverting(false);
      setConversionProgress(null);
    }
  };

  const isLanguageConverted = (languageCode) => {
    return convertedLanguages.includes(languageCode) || conversionStatus[languageCode]?.converted;
  };

  const isLanguageConverting = (languageCode) => {
    return conversionStatus[languageCode]?.converting;
  };

  // Helper popover content
  const helperContent = (
    <div className="helper-content">
      <p><strong>How it works:</strong></p>
      <ol className="helper-steps">
        <li><strong>Select</strong> a language from the list </li>
        <li><strong>Click "Convert Now"</strong> to generate audio for all course content</li>
        <li><strong>Wait</strong> for the conversion to complete (this may take a few seconds)</li>
      </ol>
      <p className="helper-warning">
        <strong>Note:</strong> Do not close the page while conversion is in progress.
      </p>
    </div>
  );

  return (
    <div className="language-selector-container-new">
      <div className="language-selector-header-new">
        <div className="language-selector-title-new">
          
          <h3>Select your preferred language for course audio</h3>
          <HelperPopover content={helperContent} openOnMount={true} />
        </div>
        
      </div>
      
      <div className="language-selector-body-new">
        <div className="language-dropdown-section">
          <div className="language-control-card-new">
            <div className="selected-language-controls">
              <div className="language-dropdown-wrapper">
                <label className="dropdown-label">Select Language</label>
                <div className="dropdown-container">
                  <select
                    className="language-dropdown-select"
                    value={selectedLanguage}
                    onChange={(e) => handleLanguageSelect(e.target.value)}
                    disabled={converting}
                  >
                    <option value="">-- Select --</option>
                    {availableLanguages.map((voice) => (
                      <option 
                        key={voice.languageCode} 
                        value={voice.languageCode}
                        disabled={isLanguageConverting(voice.languageCode)}
                      >
                        {getDropdownDisplayName(voice.languageCode)}
                        {isLanguageConverted(voice.languageCode) ? ' ✓' : ''}
                        {isLanguageConverting(voice.languageCode) ? ' (Converting...)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="dropdown-arrow">▼</div>
                </div>
              </div>
              
<div className="selected-language-display">
  <div className="selected-language-info">
    <span className="selected-language-flag">
      {selectedLanguage ? getLanguageFlag(selectedLanguage) : '🌐'} 
    </span>
    <div className="selected-language-text">
      <span className="selected-language-name">
        {selectedLanguage ? getLanguageDisplayName(selectedLanguage) : 'Select a language'}
      </span>
      <span className="selected-language-code">
        {selectedLanguage || 'Not selected'}
      </span>
    </div>
  </div>
</div>
              
              <div className="convert-action-section-new">
                <button
                  className={`convert-button-new ${!selectedLanguage || isLanguageConverted(selectedLanguage) ? 'disabled' : ''}`}
                  onClick={handleConvertClick}
                  disabled={!selectedLanguage || isLanguageConverted(selectedLanguage) || converting}
                >
                  {isLanguageConverting(selectedLanguage) ? (
                    <>
                      <div className="button-spinner-small"></div>
                      Converting...
                    </>
                  ) : isLanguageConverted(selectedLanguage) ? (
                    <>
                      <span className="converted-icon">✓</span>
                      Converted
                    </>
                  ) : (
                    <>
                      <RefreshCw size={16} />
                      Convert Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {converting && conversionProgress && (
          <div className="conversion-progress-panel-new">
            <div className="progress-panel-header-new">
              <div className="progress-title-new">
                <h4>
                  Converting to {getLanguageDisplayName(conversionProgress.language)}
                </h4>
                <span className="progress-subtitle-new">
                  This may take a few seconds...
                </span>
              </div>
              <span className="progress-percent-new">{conversionProgress.percent}%</span>
            </div>
            
            <div className="progress-details-new">
              <div className="progress-bar-main-new">
                <div 
                  className="progress-fill-main-new" 
                  style={{ width: `${conversionProgress.percent}%` }}
                ></div>
              </div>
              
              <div className="progress-stats-new">
                <div className="stat-item-new">
                  <span className="stat-label-new">Status:</span>
                  <span className="stat-value-new">{conversionProgress.message}</span>
                </div>
                <div className="stat-item-new">
                  <span className="stat-label-new">Progress:</span>
                  <span className="stat-value-new">
                    {conversionProgress.currentItem} / {conversionProgress.totalItems} items
                  </span>
                </div>
                {conversionProgress.estimatedTime && (
                  <div className="stat-item-new">
                    <span className="stat-label-new">Estimated:</span>
                    <span className="stat-value-new">{conversionProgress.estimatedTime}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Conversion Confirmation Modal */}
      {showConvertConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Language Conversion</h3>
              <button 
                className="modal-close"
                onClick={() => setShowConvertConfirmation(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="confirmation-icon">⚠️</div>
              <p className="confirmation-text">
                You are about to convert <strong>all course content</strong> to 
                <strong> {getLanguageDisplayName(selectedLanguage)}</strong>.
              </p>
              <p className="confirmation-details">
                This process may take a few seconds. The page will automatically 
                refresh when complete.
              </p>
              <div className="confirmation-stats">
                <div className="stat-row">
                  <span className="stat-label">Selected Language:</span>
                  <span className="stat-value">{getLanguageDisplayName(selectedLanguage)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-label">Estimated Time:</span>
                  <span className="stat-value">2-5 seconds</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn cancel"
                onClick={() => setShowConvertConfirmation(false)}
              >
                Cancel
              </button>
              <button 
                className="modal-btn confirm"
                onClick={confirmConversion}
              >
                Start Conversion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NavigationBar = ({ user, onNavigate, currentPage, onSearch }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }
  }, [isMobileMenuOpen]);

  const handleNavClick = (page) => {
    onNavigate(page);
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (localSearchQuery.trim()) {
      navigate('/MHMV-profile/search-results', { 
        state: { keyword: localSearchQuery } 
      });
      setIsMobileMenuOpen(false);
      setIsUserMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth(app);
      await signOut(auth);
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className="professional-nav-bar">
      <div className="nav-container">
        <div className="nav-left">
          <div className="back-button-wrapper">
            <BackButton text="Back" className="ghost" />
          </div>
          
          <div className="nav-brand" onClick={() => handleNavClick('home')}>
            <div className="logo">
              <span className="logo-text">MHMV</span>
            </div>
          </div>
        </div>

        <div className="nav-center">
          <form className="nav-search-bar" onSubmit={handleSearchSubmit}>
            <div className="search-input-wrapper">
              <span className="search-icon"></span>
              <input
                type="text"
                className="search-input"
                placeholder="Search for courses"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
              />
              {localSearchQuery && (
                <button 
                  type="button" 
                  className="clear-search"
                  onClick={() => {
                    setLocalSearchQuery('');
                    onSearch('');
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            <button type="submit" className="search-submit-btn">
              Search
            </button>
          </form>
        </div>

        <div className="nav-right">
          <div className="nav-tabs">
            <button 
              className={`nav-tab ${currentPage === 'home' ? 'active' : ''}`}
              onClick={() => handleNavClick('home')}
            >
              Home
            </button>
            <button 
              className={`nav-tab ${currentPage === 'manage-courses' ? 'active' : ''}`}
              onClick={() => handleNavClick('manage-courses')}
            >
              Manage Courses
            </button>
          </div>

          <div className="nav-icons">
            <button className="nav-icon-btn" title="Wishlist">
              <Heart size={18} />
            </button>
            <button className="nav-icon-btn" title="Cart">
              <ShoppingCart size={20} />
              <span className="icon-badge">1</span>
            </button>
            <button className="nav-icon-btn" title="Notifications">
              <Bell size={20} />
              <span className="icon-badge">2</span>
            </button>

            {user ? (
              <div className="user-menu-container">
                <button 
                  className="user-avatar-btn"
                  onClick={handleUserMenuToggle}
                >
                  <div className="user-avatar-small">
                    {user.email ? user.email[0].toUpperCase() : 'U'}
                  </div>
                </button>
                
                {isUserMenuOpen && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-header">
                      <div className="user-info-section">
                        <div className="user-avatar-large">
                          {user.email ? user.email[0].toUpperCase() : 'U'}
                        </div>
                        <div className="user-details">
                          <div className="user-name">
                            {user.displayName || user.email?.split('@')[0]}
                          </div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="dropdown-section">
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('upload')}
                      >
                        <Upload size={18} />
                        Upload Courses
                      </button>
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('courses')}
                      >
                        <Eye size={18} />
                        View Courses
                      </button>
                      <button 
                        className="dropdown-item"
                        onClick={() => handleNavClick('archive')}
                      >
                        <Eye size={18} />
                        Archived Courses
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Heart size={18} />
                        Wishlist
                      </button>
                      <button className="dropdown-item">
                        <ShoppingCart size={20} />
                        Cart
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Settings size={18} />
                        Account Settings
                      </button>
                      <button className="dropdown-item">
                        <CreditCard size={18} />
                        Payment Methods
                      </button>
                      <button className="dropdown-item">
                        <RefreshCw size={18} />
                        Subscriptions
                      </button>
                    </div>

                    <div className="dropdown-section">
                      <button className="dropdown-item">
                        <Globe size={18} />
                        Language: English
                      </button>
                      <button 
                        className="dropdown-item logout"
                        onClick={handleLogout}
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <button 
                  className="login-btn"
                  onClick={() => handleNavClick('login')}
                >
                  Log in
                </button>
                <button 
                  className="signup-btn"
                  onClick={() => handleNavClick('signup')}
                >
                  Sign up
                </button>
              </div>
            )}
          </div>

          <button 
            className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={handleMobileMenuToggle}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={handleMobileMenuToggle}></div>
        )}

        <div className={`udemy-mobile-menu ${isMobileMenuOpen ? "active" : ""}`}>
          <div className="udemy-mobile-header">
            <span className="udemy-menu-title">Menu</span>
            <button className="udemy-close-btn" onClick={handleMobileMenuToggle}>×</button>
          </div>

          <div className="udemy-user-section">
            {user ? (
              <>
                <div className="udemy-user-info">
                  <div className="udemy-user-avatar">
                    {user.email ? user.email[0].toUpperCase() : "U"}
                  </div>
                  <div className="udemy-user-details">
                    <div className="udemy-user-name">{user.displayName || user.email?.split("@")[0]}</div>
                    <div className="udemy-user-email">{user.email}</div>
                  </div>
                </div>

                <div className="udemy-menu-links">
                  <button onClick={() => handleNavClick("upload")}>Upload Courses</button>
                  <button onClick={() => handleNavClick("courses")}>View Courses</button>
                  <button onClick={() => handleNavClick("archive")}>Archived Courses</button>
                  <button>Wishlist</button>
                  <button>Cart</button>
                  <button>Account Settings</button>
                  <button>Payment Methods</button>
                  <button>Subscriptions</button>
                  <button>Language: English</button>
                  <button className="udemy-signout" onClick={handleLogout}>Sign Out</button>
                </div>
              </>
            ) : (
              <div className="udemy-auth-links">
                <button onClick={() => handleNavClick("login")}>Log In</button>
                <button onClick={() => handleNavClick("signup")}>Sign Up</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isUserMenuOpen && (
        <div className="user-menu-overlay" onClick={() => setIsUserMenuOpen(false)}></div>
      )}
    </nav>
  );
};

const ManageCoursesPage = ({ 
  currentPage, 
  handleNavigation, 
  filteredCourses, 
  handleCourseSelect, 
  startUpdate, 
  handleDelete, 
  handleArchive, 
  handleUnarchive, 
  archivingCourseId,
  user
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const handleSearch = (query) => {
    setLocalSearchQuery(query);
  };

  const handleNewCourse = () => {
    handleNavigation('upload');
  };

  return (
    <div className="manage-courses-page">
      <div className="manage-courses-layout">
        <CollapsibleSidebar 
          currentPage={currentPage} 
          onNavigate={handleNavigation}
        />
        
        <div className="manage-courses-main">
          <ManageCoursesMiniHeader user={user} onNavigate={handleNavigation} />
          <ProfessionalBanner />
          <ManageCoursesHeader 
            onNewCourse={handleNewCourse}
            searchQuery={localSearchQuery}
            onSearch={handleSearch}
          />
          
          <div className="courses-display-section">
            <h2>All Courses ({filteredCourses.length})</h2>
            {filteredCourses.length > 0 ? (
              <CoursesGridView 
                courses={filteredCourses} 
                onCourseSelect={handleCourseSelect}
                onUpdateCourse={startUpdate}
                onDeleteCourse={handleDelete}
                onArchiveCourse={handleArchive}
                onUnarchiveCourse={handleUnarchive}
                isArchiveView={false}
                archivingCourseId={archivingCourseId}
              />
            ) : (
              <div className="no-courses-message">
                <p>No courses found. Create your first course to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CourseDetailView = ({ course, onBack, user, fetchCourseDetails ,selectedLanguage,
  selectedLanguageRef,
  setSelectedLanguage,setSelectedCourse ,currentPage = 'home'}) => {
  
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedSubmodules, setExpandedSubmodules] = useState({});
  const [loadingAudio, setLoadingAudio] = useState({});
  const [audioErrors, setAudioErrors] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null);
  
  const [loadingCourseDetails, setLoadingCourseDetails] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const audioRef = useRef(null);
  const toggleModule = (moduleIndex) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleIndex]: !prev[moduleIndex]
    }));
  };

  const toggleSubmodule = (submoduleKey) => {
    setExpandedSubmodules(prev => ({
      ...prev,
      [submoduleKey]: !prev[submoduleKey]
    }));
  };
  const handleLanguageChange = (langCode) => {
  selectedLanguageRef.current = langCode; 
  setSelectedLanguage(langCode);         
};



  
  useEffect(() => {
  
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudio(null);
    setLoadingAudio({});
    setAudioErrors({});
  }, [selectedLanguage]); 

  
  
  const handleConversionComplete = async () => {
  setLoadingCourseDetails(true);
  setRefreshing(true);

  try {
    if (user?.uid) {
     
      const updatedCourses = await fetchCourseDetails(user.uid);

      if (updatedCourses && course?.courseId) {
      
        const updatedCourse = updatedCourses.find(
          c => c.courseId === course.courseId
        );

        if (updatedCourse) {
        
          setSelectedCourse(updatedCourse);
        }
      }
    }


    setSelectedLanguage(selectedLanguageRef.current);

  } catch (error) {
    console.error('Error refreshing course data:', error);
  } finally {
    setLoadingCourseDetails(false);
    setRefreshing(false);
  }
};






 
    

    const getAudioUrlForLanguage = (audioText, languageCode) => {
  if (!audioText) return null;

  const fieldMap = {
    'en-IN': ['enInAudioUrl', 'en_in_audio_url'],
    'en-US': ['enUsAudioUrl', 'en_us_audio_url'],
    'hi-IN': ['hiInAudioUrl', 'hi_in_audio_url'],
    'ta-IN': ['taInAudioUrl', 'ta_in_audio_url'],
    'te-IN': ['teInAudioUrl', 'te_in_audio_url'],
    'kn-IN': ['knInAudioUrl', 'kn_in_audio_url'],
    'ml-IN': ['mlInAudioUrl', 'ml_in_audio_url'],
    'mr-IN': ['mrInAudioUrl', 'mr_in_audio_url'],
    'gu-IN': ['guInAudioUrl', 'gu_in_audio_url'],
    'bn-IN': ['bnInAudioUrl', 'bn_in_audio_url'],
    'pa-IN': ['paInAudioUrl', 'pa_in_audio_url'],
    'ur-IN': ['urInAudioUrl', 'ur_in_audio_url'],
  };


  const fields = fieldMap[languageCode] || [];
  for (const field of fields) {
    if (audioText[field]?.trim()) {
      return audioText[field];
    }
  }

 
  if (languageCode === 'en-IN') {
    const fallbackFields = fieldMap['en-US'];
    for (const field of fallbackFields) {
      if (audioText[field]?.trim()) {
        console.warn('en-IN audio missing, falling back to en-US');
        return audioText[field];
      }
    }
  }

  return null;
};

  const playAudio = async (audioText, textKey) => {
    try {
    
      if (audioRef.current && playingAudio === textKey) {
        if (audioRef.current.paused) {
          await audioRef.current.play();
        } else {
          audioRef.current.pause();
          setPlayingAudio(null);
        }
        return;
      }

   
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      setLoadingAudio((prev) => ({ ...prev, [textKey]: true }));
      setAudioErrors((prev) => ({ ...prev, [textKey]: '' }));

    
      const languageToPlay = selectedLanguageRef.current;
      const audioUrl = getAudioUrlForLanguage(audioText, languageToPlay);

      
      console.log(`Playing ${selectedLanguage} audio for text ${textKey}:`, audioUrl);
      
      if (!audioUrl || audioUrl.trim() === '') {
        throw new Error(`Audio not available in ${getLanguageDisplayName(selectedLanguage)}`);
      }

      const idToken = await user.getIdToken();
      
      let cleanAudioUrl = audioUrl.trim();
      
 
      if (cleanAudioUrl.startsWith("https://storage.googleapis.com/")) {
        try {
          const urlObj = new URL(cleanAudioUrl);
          cleanAudioUrl = urlObj.pathname.substring(1);
        } catch (err) {
          console.warn("Failed to parse GCS URL:", err);
        }
      }

   
      if (!cleanAudioUrl.startsWith('http')) {
        cleanAudioUrl = cleanAudioUrl.replace(/^\/+/, '');
      }

      console.log("Clean audio URL:", cleanAudioUrl);
      
      const streamUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodeURIComponent(cleanAudioUrl)}`;
      console.log("Stream URL:", streamUrl);

      const response = await fetch(streamUrl, {
        headers: { 
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'audio/mpeg'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Received empty audio file');
      }

      const blobUrl = URL.createObjectURL(blob);
      const newAudio = new Audio(blobUrl);

      newAudio.onended = () => {
        setPlayingAudio(null);
        URL.revokeObjectURL(blobUrl);
      };

      newAudio.onerror = (error) => {
        console.error('Audio playback error:', error);
        setAudioErrors((prev) => ({ ...prev, [textKey]: 'Failed to play audio' }));
        setPlayingAudio(null);
        URL.revokeObjectURL(blobUrl);
      };

      newAudio.oncanplaythrough = () => {
        audioRef.current = newAudio;
        newAudio.play()
          .then(() => setPlayingAudio(textKey))
          .catch(err => {
            console.error("Audio play failed:", err);
            setAudioErrors((prev) => ({ ...prev, [textKey]: 'Playback failed' }));
          });
      };

    } catch (err) {
      console.error("Audio play error:", err);
      setAudioErrors((prev) => ({ 
        ...prev, 
        [textKey]: `Audio not available in ${getLanguageDisplayName(selectedLanguage)}` 
      }));
      setPlayingAudio(null);
    } finally {
      setLoadingAudio((prev) => ({ ...prev, [textKey]: false }));
    }
   console.log("LANG:", selectedLanguageRef.current);
console.log("AUDIO TEXT:", audioText);


  };


  const getAvailableLanguagesForText = (audioText) => {
    if (!audioText) return [];
    
    const languages = [];
    const languageFields = [
      { code: 'en-US', fields: ['enUsAudioUrl', 'en_us_audio_url'] },
      { code: 'hi-IN', fields: ['hiInAudioUrl', 'hi_in_audio_url'] },
      { code: 'ta-IN', fields: ['taInAudioUrl', 'ta_in_audio_url'] },
      { code: 'te-IN', fields: ['teInAudioUrl', 'te_in_audio_url'] },
      { code: 'kn-IN', fields: ['knInAudioUrl', 'kn_in_audio_url'] },
      { code: 'ml-IN', fields: ['mlInAudioUrl', 'ml_in_audio_url'] },
      { code: 'mr-IN', fields: ['mrInAudioUrl', 'mr_in_audio_url'] },
      { code: 'gu-IN', fields: ['guInAudioUrl', 'gu_in_audio_url'] },
      { code: 'bn-IN', fields: ['bnInAudioUrl', 'bn_in_audio_url'] },
      { code: 'pa-IN', fields: ['paInAudioUrl', 'pa_in_audio_url'] },
      { code: 'ur-IN', fields: ['urInAudioUrl', 'ur_in_audio_url'] },
      { code: 'en-IN', fields: ['enInAudioUrl', 'en_in_audio_url'] }
      
    ];
    
    languageFields.forEach(({ code, fields }) => {
      const hasAudio = fields.some(field => 
        audioText[field] && audioText[field].trim() !== ''
      );
      if (hasAudio) {
        languages.push(code);
      }
    });
    
    return languages;
  };

  const getLanguageDisplayName = (languageCode) => {
    const names = {
      'en-US': 'English (US)',
      'en-IN': 'English (India)',
      'hi-IN': 'Hindi',
      'ta-IN': 'Tamil',
      'te-IN': 'Telugu',
      'kn-IN': 'Kannada',
      'ml-IN': 'Malayalam',
      'mr-IN': 'Marathi',
      'gu-IN': 'Gujarati',
      'bn-IN': 'Bengali',
      'pa-IN': 'Punjabi',
      'ur-IN': 'Urdu'
    };
    return names[languageCode] || languageCode;
  };

  const getLanguageFlag = (languageCode) => {
    if (languageCode.endsWith('-US')) return '🇺🇸';
    if (languageCode.endsWith('-IN')) return '🇮🇳';
    return '🌐';
  };


  return (
    <div className="course-detail-view-full">
      
      <div className="course-detail-header">

        {onBack && (
        <button 
          className="back-button-full"
          onClick={onBack}
          style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            marginBottom: '20px'
          }}
        >
          ← Back to {currentPage === 'home' ? 'Home' : 'Courses'}
        </button>
      )}
      </div>
      {(loadingCourseDetails || refreshing) && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner-large"></div>
            <p>Loading updated course content...</p>
          </div>
        </div>
      )}

      <div className="course-overview-full">
        <div className="course-header-full">
          <div className="course-header-content">
            <h1 className="course-title-full">{course.title || "Course Title"}</h1>
            <div className="course-meta-full">
              {course.category && <span className="meta-tag-full category">{course.category}</span>}
              {course.language && <span className="meta-tag-full language">{course.language}</span>}
              {course.level && <span className="meta-tag-full level">{course.level}</span>}
            </div>
          </div>
        </div>

        {course.description && (
          <div className="course-description-full">
            <p>{course.description}</p>
          </div>
        )}
      </div>

  
      <LanguageSelector 
  courseId={course.courseId}
  user={user}
  currentLanguage={selectedLanguage}
  onLanguageSelect={(langCode) => {
    handleLanguageChange(langCode); 
  }}
  onConversionComplete={handleConversionComplete}
/>


      <div className="modules-section-full">
        <div className="section-header-with-language">
          <h2 className="section-title-full">Course Content</h2>
          <div className="current-language-display">
            <span className="language-label">Currently viewing:</span>
            <span className="language-badge">
              {getLanguageFlag(selectedLanguage)} {getLanguageDisplayName(selectedLanguage)}
              {loadingCourseDetails && <div className="inline-spinner"></div>}
            </span>
          </div>
        </div>
        

        {course.modules && course.modules.length > 0 ? (
          <div className="modules-list-full">
            {course.modules.map((module, moduleIndex) => (
              <div key={module.moduleId || moduleIndex} className="module-item-full">
                <div className="module-header-full" onClick={() => toggleModule(moduleIndex)}>
                  <div className="module-title-section-full">
                    <span className="expand-icon-full">
                      {expandedModules[moduleIndex] ? "▼" : "▶"}
                    </span>
                    <div className="module-info-full">
                      <h3 className="module-title-full">
                        Module {moduleIndex + 1}: {module.title || `Module ${moduleIndex + 1}`}
                      </h3>
                      {module.description && (
                        <p className="module-description-full">{module.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="module-status-full">
                    {expandedModules[moduleIndex] ? "Hide" : "Show"} Content
                  </div>
                </div>

                {expandedModules[moduleIndex] && module.submodules && (
                  <div className="submodules-list-full">
                    {module.submodules.map((submodule, subIndex) => {
                      const submoduleKey = `${moduleIndex}-${subIndex}`;
                      return (
                        <div key={submodule.subModuleId || subIndex} className="submodule-item-full">
                          <div className="submodule-header-full" onClick={() => toggleSubmodule(submoduleKey)}>
                            <div className="submodule-title-section-full">
                              <span className="expand-icon-full submodule-icon">
                                {expandedSubmodules[submoduleKey] ? "▼" : "▶"}
                              </span>
                              <div className="submodule-info-full">
                                <h4 className="submodule-title-full">
                                  {submodule.title || `Submodule ${subIndex + 1}`}
                                </h4>
                                {submodule.description && (
                                  <p className="submodule-description-full">
                                    {submodule.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {expandedSubmodules[submoduleKey] && (
                            <div className="texts-list-full">
                              {(submodule.audioTexts || []).map((audioText, textIndex) => {
                                const textKey = `${moduleIndex}-${subIndex}-${textIndex}`;
                                const text = audioText?.text || "No text available";
                                const availableLangs = getAvailableLanguagesForText(audioText);
                                const hasSelectedLangAudio =
  availableLangs.includes(selectedLanguage) ||
  (selectedLanguage === 'en-IN' && availableLangs.includes('en-US'));

                                return (
                                  <div key={textIndex} className="text-item-full">
                                    <div className="text-content-full">
                                      <span className="text-number">{textIndex + 1}.</span>
                                      <span className="text-sentence-full">{text}</span>
                                    </div>
                                    
                                    <div className="text-actions-full">
                                      <button
                                        className={`audio-button-full ${loadingAudio[textKey] ? "loading" : ""} ${!hasSelectedLangAudio ? "unavailable" : ""}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (hasSelectedLangAudio) {
                                            playAudio(audioText, textKey);
                                          } else {
                                            alert(`Audio not available in ${getLanguageDisplayName(selectedLanguage)}. Please convert this language first.`);
                                          }
                                        }}
                                        disabled={loadingAudio[textKey] || !hasSelectedLangAudio}
                                        title={hasSelectedLangAudio ? 
                                          `Play in ${getLanguageDisplayName(selectedLanguage)}` : 
                                          `Audio not available in ${getLanguageDisplayName(selectedLanguage)}`}
                                      >
                                        {loadingAudio[textKey] ? (
                                          <>
                                            <div className="button-spinner-small"></div> Loading...
                                          </>
                                        ) : playingAudio === textKey ? (
                                          <>
                                            <Pause size={18} /> Pause
                                          </>
                                        ) : (
                                          <>
                                            <Play size={18} /> Play
                                          </>
                                        )}
                                      </button>
                                    </div>
                                    {audioErrors[textKey] && (
                                      <div className="audio-error-full">⚠️ {audioErrors[textKey]}</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-content-full">
            <div className="no-content-icon">📚</div>
            <h3>No modules available</h3>
            <p>This course doesn't have any content yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const HeroSection = ({ user }) => {
  const userName = user?.displayName || user?.email?.split("@")[0] || "Learner";

  return (
    <section className="mh-hero-wrapper">
      <div className="mh-hero-top">
        <div className="mh-hero-avatar">{userName?.[0]?.toUpperCase()}</div>
        <div className="mh-hero-welcome">
          <h2>Welcome back, {userName}</h2>
          <a href="#" className="mh-hero-link">
            Edit your learning profile
          </a>
        </div>
      </div>

      <div className="mh-hero-banner">
        <div className="mh-hero-card">
          <h1 className="mh-hero-title">Grow through sound and knowledge</h1>
          <p className="mh-hero-subtitle">
            Build skills that speak — learn smarter, faster, and your way.
          </p>
          <button className="mh-hero-btn">Start Exploring</button>
        </div>

        <div className="mh-hero-image">
          <img
            src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1000&q=80"
            alt="Learning Banner"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

const ManageCoursesMiniHeader = ({ user, onNavigate }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      const auth = getAuth(app);
      await signOut(auth);
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="manage-courses-mini-header">
      <div className="mini-header-container">
        <button 
          className="home-icon-btn"
          onClick={() => onNavigate('home')}
          title="Go to Home"
        >
          <Home size={24} />
        </button>

        <button className="notification-btn">
          <Bell size={20} />
          <span className="icon-badge">2</span>
        </button>

        <div className="user-menu-container">
          <button 
            className="user-avatar-btn"
            onClick={handleUserMenuToggle}
          >
            <div className="user-avatar-small">
              {user?.email ? user.email[0].toUpperCase() : 'U'}
            </div>
          </button>
          
          {isUserMenuOpen && (
            <div className="user-dropdown-menu">
              <div className="dropdown-section">
                <button className="dropdown-item">
                  <Settings size={18} />
                  Account settings
                </button>
                <button className="dropdown-item">
                  <CreditCard size={18} />
                  Payout & tax settings
                </button>
                <button className="dropdown-item">
                  <User size={18} />
                  Public profile
                </button>
                <button className="dropdown-item">
                  <User size={18} />
                  Edit profile
                </button>
              </div>
              
              <div className="dropdown-section">
                <button className="dropdown-item">
                  Help and Support
                </button>
                <button 
                  className="dropdown-item logout"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// MAIN COURSE MANAGEMENT COMPONENT
const CourseManagement = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [updateCourseId, setUpdateCourseId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState('home');
  const [randomCourses, setRandomCourses] = useState([]);
  const [archivingCourseId, setArchivingCourseId] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [globalSelectedLanguage, setGlobalSelectedLanguage] = useState('en-US');
const globalSelectedLanguageRef = useRef('en-US');


  const navigate = useNavigate();

  
useEffect(() => {
  
  const referrer = document.referrer;
  const currentUrl = window.location.href;
  
  if (!window.location.hash && !currentUrl.includes('#')) {
    
    setCurrentPage('home');
  } else if (window.location.hash) {
    
    const hash = window.location.hash.replace('#', '');
    const validPages = ['home', 'manage-courses', 'upload', 'courses', 'archive'];
    if (validPages.includes(hash)) {
      setCurrentPage(hash);
    }
  }


  const handleHashChange = () => {
    const hash = window.location.hash.replace('#', '');
    const validPages = ['home', 'manage-courses', 'upload', 'courses', 'archive'];
    
    if (validPages.includes(hash)) {
      setCurrentPage(hash);
    } else if (!hash) {
      
      setCurrentPage('home');
    }
  };

  window.addEventListener('hashchange', handleHashChange);
  
  return () => {
    window.removeEventListener('hashchange', handleHashChange);
  };
}, []);
  const getIdToken = useCallback(async () => {
    if (user) {
      try {
        const idToken = await user.getIdToken(true);
        console.log("idtoken", idToken)
        return idToken;
        
      } catch (error) {
        console.error('Failed to get ID token:', error);
        try {
          const idToken = await user.getIdToken();
          return idToken;
        } catch (secondError) {
          console.error('Failed to get ID token even without force:', secondError);
          throw error;
        }
      }
    }
    return null;
  }, [user]);

  const fetchCourseDetails = useCallback(async (userId) => {
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        console.error('No ID token available');
        return;
      }
      const response = await axios.get(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getCourseDetailsByUser}/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          timeout: EnvironmentManager.config.requestTimeout,
        }
      );
      const processedCourses = response.data.map((course) => ({
        ...course,
        archived_course:
          course.archived_course === true ||
          course.archived_course === 'true' ||
          course.archivedCourse === true ||
          course.archivedCourse === 'true',
      }));
      setCourses(processedCourses);
      return processedCourses; 
    } catch (error) {
      console.error('Error fetching course details:', error);
      handleApiError(error, 'Failed to load course details.');
      setCourses([]);
      setFilteredCourses([]);
    }
  }, [getIdToken]);
  const fetchRandomCourses = useCallback(async () => {
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        console.error("No ID token found — user not authenticated");
        return;
      }
      const response = await axios.get(
        "https://mv-profile-959685280830.asia-south1.run.app/mv-profile/audio-courses/random-detailed",
        {
          headers: { Authorization: `Bearer ${idToken}` },
          timeout: 30000,
        }
      );
      const allRandomCourses = Array.isArray(response.data) ? response.data : [];
      setRandomCourses(allRandomCourses);
    } catch (error) {
      console.error("Error fetching random detailed courses:", error);
      setFilteredCourses([]);
    }
  }, [getIdToken]);


  const getActiveCourses = useCallback(() => {
    return courses.filter(course => !course.archived_course);
  }, [courses]);

  const getArchivedCourses = useCallback(() => {
    return courses.filter(course => course.archived_course === true);
  }, [courses]);

  useEffect(() => {
    if (currentPage === 'archive') {
      const archived = getArchivedCourses();
      setFilteredCourses(archived);
    } else {
      const active = getActiveCourses();
      setFilteredCourses(active);
    }
  }, [courses, currentPage, getActiveCourses, getArchivedCourses]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    const sourceCourses = currentPage === 'archive' ? getArchivedCourses() : getActiveCourses();
    
    if (!query.trim()) {
      setFilteredCourses(sourceCourses);
      return;
    }

    const filtered = sourceCourses.filter(course => 
      course.title?.toLowerCase().includes(query.toLowerCase()) ||
      course.description?.toLowerCase().includes(query.toLowerCase()) ||
      course.instructor?.toLowerCase().includes(query.toLowerCase()) ||
      course.category?.toLowerCase().includes(query.toLowerCase()) ||
      course.language?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCourses(filtered);
  }, [courses, currentPage, getActiveCourses, getArchivedCourses]);

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        fetchCourseDetails(currentUser.uid);
        fetchRandomCourses();
      } else {
        setUser(null);
        setCourses([]);
        setFilteredCourses([]);
        setSelectedCourse(null);
        setRandomCourses([]);
      }
    });
    return () => unsubscribe();
  }, [fetchCourseDetails, fetchRandomCourses]);

 
  const handleNavigation = (page) => {
    if (page === 'login') {
      navigate('/MHMV-profile/jobseeker-login');
    } else if (page === 'signup') {
      navigate('/MHMV-profile/modal-register');
    } else {
      if (currentPage !== page) {
      window.location.hash = `#${page}`;
      setCurrentPage(page);
      setViewMode('grid');
      setSelectedCourse(null);
      
      if (page === 'upload') {
        setUpdateCourseId(null);
        setFile(null);
      }
      }
    }
  };

  const handleApiError = (error, defaultMessage) => {
    console.error('API Error:', error);
    
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 403:
          showMessage('error', 'Access forbidden. You may not have permission.');
          break;
        case 401:
          showMessage('error', 'Authentication failed. Please log in again.');
          break;
        case 404:
          showMessage('error', 'Resource not found.');
          break;
        case 500:
          showMessage('error', 'Server error. Please try again later.');
          break;
        default:
          showMessage('error', data?.message || defaultMessage);
      }
    } else if (error.request) {
      showMessage('error', 'No response from server. Please check your connection.');
    } else {
      showMessage('error', error.message || defaultMessage);
    }
  };

  const handleArchive = async (courseId) => {
    if (!user) {
      showMessage('error', 'Please log in first');
      return;
    }

    setLoading(true);
    setArchivingCourseId(courseId);
    setMessage({ type: '', text: '' });

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        showMessage('error', 'Unable to get authentication token');
        setLoading(false);
        setArchivingCourseId(null);
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.archiveCourse}/${courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          data: {
            userid: user.uid
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );
      
      showMessage('success', `Course archived successfully: ${courseId}`);
      
      setCourses(prev => prev.map(course => 
        course.courseId === courseId 
          ? { 
              ...course, 
              archived_course: true, 
              archived_date: new Date().toLocaleString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
            }
          : course
      ));

      setTimeout(() => {
        fetchCourseDetails(user.uid);
      }, 1000);
      
    } catch (error) {
      console.error('Archive error:', error);
      handleApiError(error, 'Archive failed');
    } finally {
      setLoading(false);
      setTimeout(() => setArchivingCourseId(null), 1000);
    }
  };

  const handleUnarchive = async (courseId) => {
    if (!user) {
      showMessage('error', 'Please log in first');
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        showMessage('error', 'Unable to get authentication token');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.unarchiveCourse}/${courseId}`,
        { userid: user.uid },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );
      
      showMessage('success', `Course unarchived successfully: ${courseId}`);
      
      setCourses(prev => prev.map(course => 
        course.courseId === courseId 
          ? { 
              ...course, 
              archived_course: false, 
              unarchived_date: new Date().toLocaleString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
            }
          : course
      ));

      setTimeout(() => {
        fetchCourseDetails(user.uid);
      }, 1000);
      
    } catch (error) {
      console.error('Unarchive error:', error);
      handleApiError(error, 'Unarchive failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!user) {
      showMessage('error', 'Please log in first');
      return;
    }

    if (!file) {
      showMessage('error', 'Please select a file first');
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        showMessage('error', 'Unable to get authentication token');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('userid', user.uid);

      const response = await axios.post(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.uploadCourseDetails}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${idToken}`
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );

      showMessage('success', 'Upload successful! Course details saved.');
      await fetchCourseDetails(user.uid);
      
      setViewMode('grid');
      setFile(null);
      if (document.getElementById('file-input')) {
        document.getElementById('file-input').value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      handleApiError(error, 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (courseId) => {
    if (!user) {
      showMessage('error', 'Please log in first');
      return;
    }

    if (!file) {
      showMessage('error', 'Please select a file first');
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        showMessage('error', 'Unable to get authentication token');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('userid', user.uid);

      const response = await axios.post(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.updateCourseDetails}/${courseId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${idToken}`
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );

      showMessage('success', `Course updated successfully: ${courseId}`);
      await fetchCourseDetails(user.uid);
      
      setUpdateCourseId(null);
      setFile(null);
      if (document.getElementById('file-input')) {
        document.getElementById('file-input').value = '';
      }

    } catch (error) {
      console.error('Update error:', error);
      handleApiError(error, 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!user) {
      showMessage('error', 'Please log in first');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        showMessage('error', 'Unable to get authentication token');
        setLoading(false);
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.deleteCourseDetails}/${courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          data: {
            userid: user.uid
          },
          timeout: EnvironmentManager.config.requestTimeout
        }
      );

      showMessage('success', `Course deleted successfully: ${courseId}`);
      await fetchCourseDetails(user.uid);

    } catch (error) {
      console.error('Delete error:', error);
      handleApiError(error, 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (validTypes.includes(selectedFile.type) || 
          selectedFile.name.endsWith('.xlsx') || 
          selectedFile.name.endsWith('.xls')) {
        setFile(selectedFile);
        setMessage({ type: '', text: '' });
      } else {
        showMessage('error', 'Please select a valid Excel file (.xlsx or .xls)');
        setFile(null);
      }
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const refreshCourseData = () => {
    if (user) {
      fetchCourseDetails(user.uid);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setViewMode('detail');
  };

  const handleBackToGrid = () => {
    setViewMode('grid');
    setSelectedCourse(null);
  };

  const findExcelUrl = (course) => {
    if (!course) return null;

    const excelExtensions = [".xlsx", ".xls"];

    const knownFields = ["audioMediaUrl", "audio_media_url", "courseFile", "fileUrl", "excelFile"];
    for (const field of knownFields) {
      if (course[field] && excelExtensions.some(ext => course[field].toLowerCase().includes(ext))) {
        return course[field];
      }
    }

    for (const [key, value] of Object.entries(course)) {
      if (typeof value === "string" && excelExtensions.some(ext => value.toLowerCase().includes(ext))) {
        return value;
      }
    }

    return null;
  };

  const startUpdate = (courseId) => {
    const courseToUpdate = courses.find(course => course.courseId === courseId);

    const filePath = findExcelUrl(courseToUpdate);

    let fileName = "CourseDetails.xlsx";
    if (filePath) {
      const split = filePath.split("/");
      fileName = split[split.length - 1];
    }

    setExistingFile({
      name: fileName,
      size: "Unknown",
      uploadedAt: "Previously uploaded",
      filePath: filePath
    });

    setUpdateCourseId(courseId);
    setCurrentPage("upload");

    setFile(null);
    const input = document.getElementById("file-input");
    if (input) input.value = "";

    showMessage("info", `Please select a new Excel file to update course: ${courseId}`);
  };

  const downloadExistingFile = async () => {
    if (!existingFile?.filePath) {
      showMessage("error", "No file available to download.");
      return;
    }

    if (!user) {
      showMessage("error", "Authentication required");
      return;
    }

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        showMessage("error", "Authentication token not available");
        return;
      }

      const filePathForBackend = existingFile.filePath;
      const encodedPath = encodeURIComponent(filePathForBackend);
      const downloadUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodedPath}`;

      showMessage("info", "Preparing download...");

      const response = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = existingFile.name;
      link.click();
      URL.revokeObjectURL(blobUrl);

      showMessage("success", `Download started: ${existingFile.name}`);

    } catch (error) {
      console.error("Download error:", error);
      showMessage("error", `Failed to download: ${error.message}`);
    }
  };

  const cancelUpdate = () => {
    setUpdateCourseId(null);
    setFile(null);
    if (document.getElementById('file-input')) {
      document.getElementById('file-input').value = '';
    }
    showMessage('info', 'Update cancelled');
  };

  const HomePage = () => (
    <div className="page-container">
      <div className="page-header">
        <h1></h1>
        <p></p>
      </div>

      <div className="featured-courses">
        <h2>Recommended Courses</h2>

        {randomCourses.length > 0 ? (
          <CoursesGridView
            courses={randomCourses} 
            onCourseSelect={handleCourseSelect}
            onUpdateCourse={startUpdate}
            onDeleteCourse={handleDelete}
            onArchiveCourse={handleArchive}
            onUnarchiveCourse={handleUnarchive}
            isArchiveView={false}
            archivingCourseId={archivingCourseId}
            showOptions={false}
          />
        ) : (
          <div className="no-courses">
            <p>No random courses available right now.</p>
            <button onClick={() => fetchRandomCourses()}>
              Refresh Random Courses
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const UploadCoursesPage = ({ downloadExistingFile }) => (
    <div className="manage-courses-page">
      <div className="manage-courses-layout">
        <CollapsibleSidebar 
          currentPage={currentPage} 
          onNavigate={handleNavigation}
        />
        <div className="manage-courses-main">
          <ManageCoursesMiniHeader user={user} onNavigate={handleNavigation} />
          <div className="page-container">
            <div className="upload-section-full">
              <div className="upload-card-full">
                <h2>
                  {updateCourseId 
                    ? `Update Course: ${updateCourseId}` 
                    : 'Upload New Course'
                  }
                </h2>
                <p className="upload-description">
                  {updateCourseId 
                    ? 'Select a new Excel file to update your course content'
                    : 'Upload an Excel file to create a new audio course with modules and content'
                  }
                </p>
                {updateCourseId && existingFile && (
                  <div className="audio-existing-file-section">
                    <div className="audio-file-info">
                      <FileSpreadsheet className="audio-excel-icon" />
                      <div className="audio-file-details">
                        <p className="audio-file-name">{existingFile.name}</p>
                        <p className="audio-file-meta">
                          {existingFile.size} • Uploaded on {existingFile.uploadedAt}
                        </p>
                      </div>
                    </div>
                    <button
                      className="audio-download-btn"
                      onClick={downloadExistingFile}
                      disabled={!existingFile?.filePath}
                      title={existingFile?.filePath ? `Download ${existingFile.name}` : "File not available for download"}
                    >
                      <Download className="audio-download-icon" />
                      <span>Download</span>
                    </button>
                  </div>
                )}

                <div className="file-upload-area-full">
                  <div className="file-input-container-full">
                    <div className="upload-icon-full">📁</div>
                    <h3>Select Excel File</h3>
                    <p>Supported formats: .xlsx, .xls</p>
                    <label htmlFor="file-input" className="file-input-label-full">
                      Choose File
                    </label>
                    <input
                      id="file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="file-input-full"
                    />
                  </div>
                  
                  {file && (
                    <div className="file-selected-full">
                      <div className="file-icon">📄</div>
                      <div className="file-info-full">
                        <strong>{file.name}</strong>
                        <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="upload-actions-full">
                  {updateCourseId && (
                    <button
                      onClick={cancelUpdate}
                      className="cancel-button-full"
                      disabled={loading}
                    >
                      Cancel Update
                    </button>
                  )}
                  <button
                    onClick={updateCourseId ? () => handleUpdate(updateCourseId) : handleUpload}
                    disabled={loading || !file}
                    className="upload-button-full"
                  >
                    {loading ? (
                      <>
                        <div className="button-spinner"></div>
                        {updateCourseId ? 'Updating...' : 'Uploading...'}
                      </>
                    ) : (
                      <>
                        {updateCourseId ? 'Update Course' : 'Upload Course'}
                      </>
                    )}
                  </button>
                </div>

                {message.text && (
                  <div className={`message-full ${message.type}`}>
                    <span className="message-icon">
                      {message.type === 'success' && '✅'}
                      {message.type === 'error' && '❌'}
                      {message.type === 'info' && 'ℹ'}
                    </span>
                    {message.text}
                  </div>
                )}

                {updateCourseId && (
                  <div className="update-info-full">
                    <p><strong>Note:</strong> This will replace all existing course content with data from the new Excel file.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ViewCoursesPage = () => (
    <div className="manage-courses-page">
      <div className="manage-courses-layout">
        <CollapsibleSidebar 
          currentPage={currentPage} 
          onNavigate={handleNavigation}
        />
        <div className="manage-courses-main">
          <ManageCoursesMiniHeader user={user} onNavigate={handleNavigation} />
          <div className="page-container">
            
            
            <div className="view-section-full">
              <div className="view-header-full">
                
                
              </div>

              {filteredCourses.length > 0 ? (
                viewMode === 'grid' ? (
                  <CoursesGridView 
                    courses={filteredCourses} 
                    onCourseSelect={handleCourseSelect}
                    onUpdateCourse={startUpdate}
                    onDeleteCourse={handleDelete}
                    onArchiveCourse={handleArchive}
                    onUnarchiveCourse={handleUnarchive}
                    isArchiveView={false}
                    archivingCourseId={archivingCourseId}
                  />
                ) : (
                  <CourseDetailView
  course={selectedCourse}
  user={user}
  fetchCourseDetails={fetchCourseDetails}
  onBack={handleBackToGrid}
  selectedLanguage={globalSelectedLanguage}
  selectedLanguageRef={globalSelectedLanguageRef}
  setSelectedLanguage={setGlobalSelectedLanguage}
  setSelectedCourse={setSelectedCourse}
  currentPage={currentPage}
/>

                )
              ) : (
                <div className="no-course-full">
                  {searchQuery ? (
                    <>
                      <div className="no-course-icon">🔍</div>
                      <h3>No courses found</h3>
                      <p>No courses match your search for "{searchQuery}"</p>
                      <button 
                        onClick={() => handleSearch('')}
                        className="clear-search-button-full"
                      >
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="no-course-icon">📚</div>
                      <h3>No courses yet</h3>
                      <p>Get started by uploading your first course</p>
                      <button 
                        onClick={() => setCurrentPage('upload')}
                        className="upload-now-button-full"
                      >
                        Upload Your First Course
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ArchiveCoursesPage = () => (
    <div className="manage-courses-page">
      <div className="manage-courses-layout">
        <CollapsibleSidebar 
          currentPage={currentPage} 
          onNavigate={handleNavigation}
        />
        <div className="manage-courses-main">
          <ManageCoursesMiniHeader user={user} onNavigate={handleNavigation} />
          <div className="page-container">
            <div className="page-header">
              <h1>Archived Courses</h1>
              <p>Your archived audio courses</p>
            </div>
            
            <div className="view-section-full">
              <div className="view-header-full">
                <div className="view-title-section">
                  <h2>Archived Courses ({filteredCourses.length})</h2>
                  <p>Courses you've archived will appear here</p>
                </div>
                <div className="view-actions-full">
                  {viewMode === 'detail' && (
                    <button onClick={handleBackToGrid} className="back-button-full">
                      ← Back to Archived Courses
                    </button>
                  )}
                  <button onClick={refreshCourseData} className="refresh-button-full">
                    Refresh
                  </button>
                </div>
              </div>

              {filteredCourses.length > 0 ? (
                viewMode === 'grid' ? (
                  <CoursesGridView 
                    courses={filteredCourses} 
                    onCourseSelect={handleCourseSelect}
                    onUpdateCourse={startUpdate}
                    onDeleteCourse={handleDelete}
                    onArchiveCourse={handleArchive}
                    onUnarchiveCourse={handleUnarchive}
                    isArchiveView={true}
                    archivingCourseId={archivingCourseId}
                  />
                ) : (
                  <CourseDetailView 
                    course={selectedCourse} 
                    onBack={handleBackToGrid}
                    user={user}
                  />
                )
              ) : (
                <div className="no-course-full">
                  {searchQuery ? (
                    <>
                      <div className="no-course-icon">🔍</div>
                      <h3>No archived courses found</h3>
                      <p>No archived courses match your search for "{searchQuery}"</p>
                      <button 
                        onClick={() => handleSearch('')}
                        className="clear-search-button-full"
                      >
                        Clear Search
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="no-course-icon">📁</div>
                      <h3>No archived courses</h3>
                      <p>You haven't archived any courses yet</p>
                      <button 
                        onClick={() => setCurrentPage('courses')}
                        className="upload-now-button-full"
                      >
                        View Active Courses
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!user) {
    return (
      <div className="course-management-full">
        <NavigationBar user={user} onNavigate={handleNavigation} currentPage={currentPage} onSearch={handleSearch} />
        <div className="auth-required-full">
          <div className="auth-container-full">
            <div className="auth-icon">🔒</div>
            <h2>Authentication Required</h2>
            <p>Please log in to access course management features</p>
            <button className="auth-action-btn" onClick={() => handleNavigation('login')}>
              Sign In to Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="course-management-full">
      {currentPage === "home" && (
        <NavigationBar
          user={user}
          onNavigate={handleNavigation}
          currentPage={currentPage}
          onSearch={handleSearch}
        />
      )}
      {currentPage === "home" && viewMode === "grid" && <HeroSection user={user} />}

      <div className="main-content-full">
        {currentPage === "home" && (
          viewMode === "grid" ? (
            <HomePage />
          ) : (
            <CourseDetailView
              course={selectedCourse}
              onBack={handleBackToGrid}
              user={user}
              fetchCourseDetails={fetchCourseDetails}
              selectedLanguage={globalSelectedLanguage}
              selectedLanguageRef={globalSelectedLanguageRef}
              setSelectedLanguage={setGlobalSelectedLanguage}
              setSelectedCourse={setSelectedCourse}
              currentPage={currentPage} 
            />
          )
        )}
        {currentPage === "manage-courses" && (
          <ManageCoursesPage 
            currentPage={currentPage}
            handleNavigation={handleNavigation}
            filteredCourses={filteredCourses}
            handleCourseSelect={handleCourseSelect}
            startUpdate={startUpdate}
            handleDelete={handleDelete}
            handleArchive={handleArchive}
            handleUnarchive={handleUnarchive}
            archivingCourseId={archivingCourseId}
            user={user} 
          />
        )}

        {currentPage === "upload" && <UploadCoursesPage downloadExistingFile={downloadExistingFile} />}
        {currentPage === "courses" && <ViewCoursesPage />}
        {currentPage === "archive" && <ArchiveCoursesPage />}
      </div>
    </div>
  );
};

export default CourseManagement;